"use client";

import { useEffect, useRef, useState } from "react";
import { getGridNeighbors } from "@/lib/grid-neighbors";

type ColorOption = { id: string; name: string; swatchHex: string };

export const DEFAULT_GRID_PRELOAD_STEPS = 1;
export const DEFAULT_SEQUENCE_PRELOAD_STEPS = 2;

// Resolves WHICH colors to background-preload next (see ColorImagePreloader for what happens with the
// result), and hands back the ref callback to attach to each swatch button so their real rendered
// positions are available to measure. Two strategies, chosen automatically:
//
//  - GRID (the picker is actually open, so there's something real to measure): the current selection's
//    real geometric up/down/left/right neighbors, walked `gridSteps` positions out in each of those
//    (up to 4) directions -- e.g. `gridSteps=1` (the default) preloads at most 4 swatches: whichever of
//    up/down/left/right genuinely exist at the grid's edges/corners. Measured from actual rendered
//    positions (see grid-neighbors.ts), not assumed from array-index math -- correct regardless of how
//    many columns the CSS happens to wrap to on this screen.
//  - SEQUENCE (the picker is closed -- nothing rendered to measure a grid position from): falls back to
//    `sequenceSteps` before/after in the flat colors[] array, same as this preloader's original behavior.
//    Reasonable as a fallback since there's no visual grid for a shopper to be looking at yet anyway.
export function useColorPreloadTargets({
  colors, currentColorId, gridVisible,
  gridSteps = DEFAULT_GRID_PRELOAD_STEPS, sequenceSteps = DEFAULT_SEQUENCE_PRELOAD_STEPS,
}: {
  colors: ColorOption[];
  currentColorId: string | undefined;
  gridVisible: boolean;
  gridSteps?: number;
  sequenceSteps?: number;
}) {
  const swatchElsRef = useRef(new Map<string, HTMLElement>());
  const [targets, setTargets] = useState<ColorOption[]>([]);

  const registerSwatchEl = (id: string, el: HTMLElement | null) => {
    if (el) swatchElsRef.current.set(id, el);
    else swatchElsRef.current.delete(id);
  };

  useEffect(() => {
    if (!currentColorId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting for a new/absent selection, same tradeoff as this codebase's other per-selection reset effects
      setTargets([]);
      return;
    }

    if (gridVisible && swatchElsRef.current.size > 0) {
      const items = colors
        .filter((c) => swatchElsRef.current.has(c.id))
        .map((c) => ({ id: c.id, el: swatchElsRef.current.get(c.id)! }));
      const ids = new Set<string>();
      for (const direction of ["left", "right", "up", "down"] as const) {
        let cursorId = currentColorId;
        for (let step = 0; step < gridSteps; step++) {
          const nextId = getGridNeighbors(cursorId, items)[direction];
          if (!nextId) break;
          ids.add(nextId);
          cursorId = nextId;
        }
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect -- recomputing after layout for a new selection/visibility, same tradeoff as above
      setTargets(colors.filter((c) => ids.has(c.id)));
      return;
    }

    // Sequence fallback -- no grid rendered to measure right now.
    const index = colors.findIndex((c) => c.id === currentColorId);
    if (index === -1) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
      setTargets([]);
      return;
    }
    const offsets = Array.from({ length: sequenceSteps }, (_, i) => i + 1);
    const nearby = [...offsets.map((n) => index - n).reverse(), ...offsets.map((n) => index + n)]
      .map((i) => colors[i])
      .filter((c): c is ColorOption => !!c);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    setTargets(nearby);
  }, [colors, currentColorId, gridVisible, gridSteps, sequenceSteps]);

  return { targets, registerSwatchEl };
}
